import { usePublicProductRecommendations } from "@babackapp/private-api-client/client/use-public-product-recommendations";
import type { PublicProductRecommendation } from "@babackapp/private-api-client/client/use-public-product-recommendations";
import { useExchangeSuggestions } from "@babackapp/private-api-client/client/use-exchange-suggestions";
import type { ExchangeReason } from "@babackapp/private-api-client/client/use-exchange-suggestions";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { useReturnReasons } from "@babackapp/return-widget-components/hooks/use-return-reasons";
import { useReturnableItems } from "@babackapp/return-widget-components/hooks/use-returnable-items";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useWidgetParams } from "./use-widget-params";

export type SuggestionItem = {
  id: string;
  label: string;
  subtitle?: string;
  imageUrl: string | null;
  variantId?: string;
};

export type SuggestionResult = {
  items: SuggestionItem[];
  currentVariantTitle: string | null;
} & (
  | { type: "sizing"; isLoading: boolean }
  | { type: "products"; isLoading: boolean }
);

const SIZING_CATEGORIES = new Set<ExchangeReason>([
  "SIZE_TOO_LARGE",
  "SIZE_TOO_SMALL",
]);

const PRODUCT_REC_CATEGORIES = new Set(["STYLE", "UNWANTED"]);

function buildProductItems(
  recs: PublicProductRecommendation[],
): SuggestionItem[] {
  return recs.slice(0, 6).map((rec) => ({
    id: rec.targetProductId,
    label: rec.targetProductTitle ?? "",
    imageUrl: rec.targetImageUrl,
  }));
}

export function useResolutionSuggestions(): SuggestionResult | null {
  const { lineItemId = "" } = useParams<{ lineItemId: string }>();
  const { orderId, shopId, shopInternalId, shopSlug } = useWidgetParams();
  const t = useCustomText();
  const draft = useDraftReturn(orderId);
  const reasons = useReturnReasons(shopId);
  const returnableItems = useReturnableItems(orderId, shopSlug);

  const draftItem = draft?.lineItems.find((li) => li.lineItemId === lineItemId);
  const lineItem = returnableItems.find((li) => li.line_item_id === lineItemId);
  const selectedReason =
    reasons.find((r) => r.id === draftItem?.reasonId) ?? null;

  const sizingReason = SIZING_CATEGORIES.has(
    selectedReason?.standardCategory as ExchangeReason,
  )
    ? (selectedReason?.standardCategory as ExchangeReason)
    : null;

  const productId = lineItem?.product?.productId ?? null;
  const currentVariantId = lineItem?.variant?.variant_id ?? null;
  const currentVariantTitle = lineItem?.variant?.title ?? null;

  const { data: exchangeItems = [], isLoading: isLoadingSizing } =
    useExchangeSuggestions(
      shopInternalId,
      sizingReason ? productId : null,
      sizingReason ? currentVariantId : null,
      sizingReason,
    );

  const isProductRecReason = PRODUCT_REC_CATEGORIES.has(
    selectedReason?.standardCategory ?? "",
  );

  const { data: productRecs = [], isLoading: isLoadingRecs } =
    usePublicProductRecommendations(
      shopInternalId,
      isProductRecReason ? productId : null,
    );

  const sizingItems = useMemo(
    () =>
      exchangeItems.slice(0, 6).map((item) => ({
        id: item.variantId,
        label: item.direction === "above" ? t("suggestions.sizing.above") : t("suggestions.sizing.below"),
        subtitle: item.sizeLabel,
        imageUrl: item.imageUrl ?? lineItem?.image ?? null,
        variantId: item.variantId,
      })),
    [exchangeItems, lineItem?.image, t],
  );

  const productItems = useMemo(
    () => buildProductItems(productRecs),
    [productRecs],
  );

  if (!selectedReason) return null;

  if (sizingReason) {
    return {
      type: "sizing",
      items: sizingItems,
      isLoading: isLoadingSizing,
      currentVariantTitle,
    };
  }

  if (!isProductRecReason || !productId) return null;
  return {
    type: "products",
    items: productItems,
    isLoading: isLoadingRecs,
    currentVariantTitle,
  };
}
